import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, PhoneCall, PhoneMissed, PhoneOff, PhoneIncoming,
  MessageSquare, RefreshCw
} from "lucide-react";
import { toast } from "sonner";
import BottomNav from "@/components/BottomNav";
import Header from "@/components/Header";

const BACKEND_URL = window.APP_CONFIG?.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const getSessionToken = () => localStorage.getItem("session_token");

/** Human-friendly relative timestamp — "2m ago", "3h ago", "yesterday", or a full date. */
function relTime(iso) {
  if (!iso) return "";
  const then = new Date(iso);
  const now = new Date();
  const diffMin = Math.round((now - then) / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.round(diffH / 24);
  if (diffD === 1) return "yesterday";
  if (diffD < 7) return `${diffD}d ago`;
  return then.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function statusIcon(call) {
  if (call.was_missed) return <PhoneMissed className="w-5 h-5 text-red-500" />;
  if (call.status === "completed") return <PhoneCall className="w-5 h-5 text-green-600" />;
  return <PhoneOff className="w-5 h-5 text-zinc-400" />;
}

export default function CallLog() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState({ calls: [], count: 0, missed_count: 0 });
  const [showMissedOnly, setShowMissedOnly] = useState(false);

  useEffect(() => {
    const init = async () => {
      const token = getSessionToken();
      if (!token) { navigate("/login"); return; }
      try {
        const me = await axios.get(`${API}/auth/me`,
          { headers: { Authorization: `Bearer ${token}` } });
        if (me.data.role !== "Admin") {
          toast.error("Admin only");
          navigate("/settings");
          return;
        }
        setUser(me.data);
        await loadCalls();
      } catch (_e) {
        navigate("/login");
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  const loadCalls = async () => {
    setRefreshing(true);
    const token = getSessionToken();
    try {
      const { data: resp } = await axios.get(`${API}/call-log`,
        { headers: { Authorization: `Bearer ${token}` } });
      setData(resp);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load calls");
    } finally {
      setRefreshing(false);
    }
  };

  const openConversation = (call) => {
    if (call.caller_kind === "customer" && call.caller_id) {
      navigate(`/messages?customer_id=${call.caller_id}`);
    } else {
      // No known customer — jump to Messages with phone prefilled
      navigate(`/messages?phone=${encodeURIComponent(call.from_number)}`);
    }
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-zinc-200 border-t-[#207024]"></div>
      </div>
    );
  }

  const visible = showMissedOnly ? data.calls.filter(c => c.was_missed) : data.calls;

  return (
    <div className="min-h-screen bg-white">
      <Header user={user} />
      <div className="max-w-lg mx-auto pb-24 p-4">
        <button
          onClick={() => navigate("/settings")}
          className="flex items-center gap-2 text-zinc-600 hover:text-zinc-900 mb-4"
          data-testid="back-button"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="font-inter">Back to Settings</span>
        </button>

        <div className="flex items-start justify-between mb-6 gap-3">
          <div>
            <h1 className="font-barlow text-3xl font-bold text-zinc-950 uppercase mb-1">Call Log</h1>
            <p className="font-inter text-zinc-600 text-sm">
              Recent calls to your ScreenzZ number
            </p>
          </div>
          <button
            onClick={loadCalls}
            disabled={refreshing}
            data-testid="refresh-call-log"
            className="p-2 rounded-lg bg-zinc-100 hover:bg-zinc-200 text-zinc-700 disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
          </button>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 gap-3 mb-5">
          <button
            onClick={() => setShowMissedOnly(false)}
            data-testid="filter-all-calls"
            className={`text-left p-3 rounded-lg border-2 transition-colors ${
              !showMissedOnly ? "border-[#207024] bg-emerald-50" : "border-zinc-100 hover:border-zinc-200"
            }`}
          >
            <div className="text-2xl font-bold text-zinc-900">{data.count}</div>
            <div className="text-xs text-zinc-500 uppercase tracking-wide">Last {data.days || 30} days</div>
          </button>
          <button
            onClick={() => setShowMissedOnly(true)}
            data-testid="filter-missed-calls"
            className={`text-left p-3 rounded-lg border-2 transition-colors ${
              showMissedOnly ? "border-red-500 bg-red-50" : "border-zinc-100 hover:border-zinc-200"
            }`}
          >
            <div className="text-2xl font-bold text-red-600">{data.missed_count}</div>
            <div className="text-xs text-zinc-500 uppercase tracking-wide">Missed</div>
          </button>
        </div>

        <div className="space-y-2">
          {visible.map((c) => (
            <div
              key={c.call_sid}
              className={`rounded-lg border p-3 ${c.was_missed ? "border-red-100 bg-red-50/40" : "border-zinc-100 bg-white"}`}
              data-testid={`call-row-${c.call_sid}`}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white border flex items-center justify-center shrink-0">
                  {statusIcon(c)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-semibold text-zinc-900 truncate">
                      {c.caller_name || c.from_formatted || c.from_number}
                    </div>
                    {c.caller_kind && (
                      <span className="text-[10px] uppercase tracking-wide font-bold text-zinc-400">
                        {c.caller_kind}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-zinc-500 flex items-center gap-2">
                    {c.caller_name && <span className="font-mono">{c.from_formatted}</span>}
                    {c.from_city && <span>· {c.from_city}, {c.from_state}</span>}
                    <span>· {relTime(c.started_at)}</span>
                    {c.duration_seconds > 0 && <span>· {c.duration_seconds}s</span>}
                  </div>
                </div>
              </div>
              <div className="flex gap-2 mt-2">
                <a
                  href={`tel:${c.from_number}`}
                  data-testid={`call-back-${c.call_sid}`}
                  className="flex-1 h-9 rounded-md bg-[#207024] hover:bg-[#1a5c1d] text-white text-sm font-semibold flex items-center justify-center gap-1.5"
                >
                  <PhoneIncoming className="w-3.5 h-3.5" />
                  Call Back
                </a>
                <button
                  onClick={() => openConversation(c)}
                  data-testid={`text-caller-${c.call_sid}`}
                  className="flex-1 h-9 rounded-md bg-white border-2 border-[#207024] text-[#207024] text-sm font-semibold flex items-center justify-center gap-1.5"
                >
                  <MessageSquare className="w-3.5 h-3.5" />
                  Text
                </button>
              </div>
            </div>
          ))}

          {visible.length === 0 && (
            <div className="text-center py-12 bg-zinc-50 rounded-xl border-2 border-dashed border-zinc-200">
              <PhoneCall className="w-10 h-10 text-zinc-300 mx-auto mb-2" />
              <p className="text-zinc-500 text-sm">
                {showMissedOnly ? "No missed calls in the last 30 days." : "No recent calls."}
              </p>
            </div>
          )}
        </div>
      </div>
      <BottomNav />
    </div>
  );
}
